import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import FollowingFeedWithSortByStorageSnippetsQuery, {
	type FollowingFeedWithSortByStorageSnippetsQuery$data,
	type FollowingFeedWithSortByStorageSnippetsQuery$variables,
} from './__generated__/FollowingFeedWithSortByStorageSnippetsQuery.graphql';
import useFollowingFeedWithSortByStorageFeedContentQuery, {
	type useFollowingFeedWithSortByStorageFeedContentQuery$data,
	type useFollowingFeedWithSortByStorageFeedContentQuery$variables,
} from './__generated__/useFollowingFeedWithSortByStorageFeedContentQuery.graphql';
import useFeedTypeSelectorRefreshQuery, {
	type useFeedTypeSelectorRefreshQuery$data,
	type useFeedTypeSelectorRefreshQuery$variables,
} from './__generated__/useFeedTypeSelectorRefreshQuery.graphql';
import usePopularFeedWithTimeGranularityStorageQuery, {
	type usePopularFeedWithTimeGranularityStorageQuery$data,
	type usePopularFeedWithTimeGranularityStorageQuery$variables,
} from './__generated__/usePopularFeedWithTimeGranularityStorageQuery.graphql';

export const preloadFeed = ({ isLoggedIn }) => {
	// If the user is not logged in we show the following feed by default
	// so only preload that feed. If the user is logged in preload both
	// feeds and the feed preference

	const followingFeedQuery = UNSAFE_expValEqualsNoExposure(
		'confluence_ai_snippets_in_following_popular_feeds',
		'isEnabled',
		true,
	)
		? queryAgg<
				FollowingFeedWithSortByStorageSnippetsQuery$data,
				FollowingFeedWithSortByStorageSnippetsQuery$variables
			>({
				query: FollowingFeedWithSortByStorageSnippetsQuery,
				variables: {
					first: expVal('confluence_home_feed_initial_page_size', 'following_ssr', 5),
					includeGeneratedBy: fg('cc-activation-use-generated-by-content-property'),
				},
			})
		: query<
				useFollowingFeedWithSortByStorageFeedContentQuery$data,
				useFollowingFeedWithSortByStorageFeedContentQuery$variables
			>({
				query: useFollowingFeedWithSortByStorageFeedContentQuery,
				variables: {
					first: expVal('confluence_home_feed_initial_page_size', 'following_ssr', 5),
					includeGeneratedBy: fg('cc-activation-use-generated-by-content-property'),
				},
			});

	const popularFeedQuery = UNSAFE_expValEqualsNoExposure(
		'confluence_ai_snippets_in_following_popular_feeds',
		'isEnabled',
		true,
	)
		? [
				queryAgg<
					usePopularFeedWithTimeGranularityStorageQuery$data,
					usePopularFeedWithTimeGranularityStorageQuery$variables
				>({
					query: usePopularFeedWithTimeGranularityStorageQuery,
					variables: {
						first: fg('confluence_ai_feeds_home_banner')
							? 10
							: expVal('confluence_home_feed_initial_page_size', 'popular_ssr', 5),
						includeGeneratedBy: fg('cc-activation-use-generated-by-content-property'),
					},
				}),
				queryAgg<useFeedTypeSelectorRefreshQuery$data, useFeedTypeSelectorRefreshQuery$variables>({
					query: useFeedTypeSelectorRefreshQuery,
				}),
			]
		: [
				query<
					usePopularFeedWithTimeGranularityStorageQuery$data,
					usePopularFeedWithTimeGranularityStorageQuery$variables
				>({
					query: usePopularFeedWithTimeGranularityStorageQuery,
					variables: {
						first: fg('confluence_ai_feeds_home_banner')
							? 10
							: expVal('confluence_home_feed_initial_page_size', 'popular_ssr', 5),
						includeGeneratedBy: fg('cc-activation-use-generated-by-content-property'),
					},
				}),
				query<useFeedTypeSelectorRefreshQuery$data, useFeedTypeSelectorRefreshQuery$variables>({
					query: useFeedTypeSelectorRefreshQuery,
				}),
			];
	const tasks: Array<Promise<any>> = [followingFeedQuery];
	if (isLoggedIn) {
		tasks.push(...popularFeedQuery);
	}
	return waitForAllReturnWhenError(tasks);
};
